import React, { useState, useContext, useRef, useEffect } from "react";
import QRCode from "react-qr-code";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { inpS, crd } from "../../constants/styles";
import Ic from "../common/Ic";
import CopyBtn from "../common/CopyBtn";
import { buildUpiUrl, isValidUpiId } from "../../utils/deepLinks";
import { monthSortValue } from "../../utils/dateFormat";
import { getBankRows } from "../../utils/bankDetails";

// Tenant "Pay Now" page.
// Manual payment flow only: UPI QR, Cash, or Bank transfer.
// All payments are recorded as `pending` and require landlord approval
// before becoming "paid" (mirrors the inline panel in TenDash). Razorpay is
// intentionally NOT used for rent — it routes to the single platform merchant
// account and cannot split to individual landlords without Razorpay Route.
function TenPayNow() {
  const { t, h, pays, setPays, ccs, mts, tens, go, flash, currUserId, addNotif, settings, refreshData } = useContext(Ctx);
  const ct = currUserId;
  // Maintenance charges live in their own "🔧"-prefixed payment rows. Aggregate
  // rent + all maintenance dues so this page mirrors what TenDash's "Pay Now"
  // promised. `rentRow` keeps the existing single-row code paths working
  // (myCC lookup, txn.mo, etc.); `maintRows` holds the side charges.
  const allDues = pays.filter(x => x.tid === ct && (x.st === "due" || x.st === "partial"));
  // ALL unpaid rent months, oldest first — previously only the first row
  // find() returned was payable, so with 2+ months due the older ones
  // were invisible here and could never be paid.
  const rentRows = allDues.filter(x => !(x.mo || "").startsWith("🔧"))
    .sort((a, b) => monthSortValue(a.mo) - monthSortValue(b.mo));
  const rentRow = rentRows[0] || null;
  const maintRows = allDues.filter(x => (x.mo || "").startsWith("🔧"));
  const dp = rentRow || maintRows[0] || null;
  const me = tens.find(x => x.id === ct);
  const [showCommonSplit, setShowCommonSplit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [payMth, setPayMth] = useState("upi"); // upi | cash | bank
  const [upiOpened, setUpiOpened] = useState(false);
  const payLockRef = useRef(false); // Instant lock to prevent double-clicks
  // Phase 16: online rent (Cashfree). `onlineAvailable` reflects whether
  // the landlord has connected their OWN Cashfree account; only then do
  // we surface the "Online" method (money settles straight to them).
  const [onlineAvailable, setOnlineAvailable] = useState(false);
  const [cfBusy, setCfBusy] = useState(false);
  // Online pays the primary due row (oldest rent month, else first maint)
  // in full — gateway payments settle the whole row, not partial/advance.
  const onlineAmt = (rentRows[0] || maintRows[0]) ? (Number((rentRows[0] || maintRows[0]).amt || 0) + Number((rentRows[0] || maintRows[0]).com || 0)) : 0;
  const rentAmt = rentRows.reduce((s, r) => s + (r.amt || 0), 0);
  const rentCom = rentRows.reduce((s, r) => s + (r.com || 0), 0);
  const maintTotal = maintRows.reduce((s, m) => s + (m.amt || 0) + (m.com || 0), 0);
  const commonAndMaint = rentCom + maintTotal;
  const grossTotal = rentAmt + commonAndMaint;
  // Already-paid counts verified txns across all unpaid rent rows (maint
  // rows are typically zero-amt with no prior partials).
  const alreadyPaid = rentRows.reduce((s, r) => s + (r.txns || []).reduce((x, tx) => x + (tx.status === "verified" ? tx.amt : 0), 0), 0);
  // Amounts already submitted and awaiting landlord approval must not be
  // billed again. Without this netting, a tenant with a pending submission
  // saw the FULL amount in the UPI link, paid real money, and the submit
  // handler (which skips pending rows) recorded nothing.
  const pendingAmt = rentRows.reduce((s, r) => s + (r.pending?.amt || 0), 0) + maintRows.reduce((s, m) => s + (m.pending?.amt || 0), 0);
  const tot = Math.max(0, grossTotal - alreadyPaid - pendingAmt);
  const [payType, setPayType] = useState("full"); // full | partial | advance
  const [payAmt, setPayAmt] = useState(0);

  // Find CC record matching the due payment month
  const myCC = dp ? (ccs.find(c => c.mo === dp.mo) || ccs[0] || null) : (ccs[0] || null);
  // Settlement status of the 🔧 payment row behind an applied charge.
  // Listing the charge bare made an already-paid one (e.g. auto-covered
  // from advance credit) look like an outstanding due that mysteriously
  // wasn't counted in the Common total.
  const maintChargeSt = (mc) => {
    const row = pays.find(p => p.tid === ct && typeof p.id === "string" && p.id.startsWith(`pay_maint_${mc.id}_`));
    if (!row) return null;
    return row.st === "paid" ? "paid" : row.pending ? "pending" : "due";
  };
  const maintChargePill = (mc) => {
    const st = maintChargeSt(mc);
    if (!st) return null;
    const c = st === "paid" ? "#16a34a" : st === "pending" ? (h.wn || "#d97706") : (h.rd || "#ef4444");
    return <span style={{ marginLeft: 6, padding: "1px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, background: `${c}15`, color: c }}>{st === "paid" ? "✓ Paid" : st === "pending" ? "⏳ Pending" : "Due"}</span>;
  };
  // Only list charges that still affect money owed — settled ones are
  // already visible under My Payments, so repeating them here read as a
  // phantom outstanding amount.
  const myMaintCharges = mts.filter(m => m.exp_amt > 0 && m.exp_applied && m.exp_target === ct)
    .filter(mc => maintChargeSt(mc) !== "paid");
  const myShare = myCC && myCC.n > 0 ? Math.round(
    ((myCC.items && myCC.items.length > 0)
      ? myCC.items.reduce((s, it) => s + Number(it.amount || 0), 0)
      : (myCC.eb + myCC.cl)) / myCC.n
  ) : 0;

  // Set default pay amount to total on first render
  React.useEffect(() => { if (tot > 0 && payAmt === 0) setPayAmt(tot); }, [tot]);
  // Default advance amount when no due payment
  React.useEffect(() => { if (!dp && payAmt === 0) setPayAmt(1000); }, [dp]);

  // Ask the server whether this tenant's landlord accepts online rent
  // (a verified Cashfree connection). Drives the "Online" method button.
  useEffect(() => {
    let alive = true;
    api.getOnlineRentAvailability()
      .then(d => { if (alive && d && d.success) setOnlineAvailable(!!d.available); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // ── Shared payment-method picker ──────────────────────────────────
  // Renders UPI QR / Cash / Bank details based on selected `payMth`.
  // `amount` is the numeric amount to encode into the UPI deep link.
  // `onConfirmNonUpi` is fired for cash/bank methods when the tenant taps Submit.
  // For UPI, the tenant first opens their UPI app (deep link), then confirms
  // with an "I've paid" button which calls `onConfirmUpi`.
  const renderMethodPanel = ({ amount, onConfirmUpi, onConfirmNonUpi, confirmDisabled, onOnline, onlineAmount = 0 }) => (
    <>
      <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.method || "Method"}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {[...((onlineAvailable && onOnline) ? ["online"] : []), "upi", "cash", "bank"].map(m => {
          const isOnline = m === "online";
          const sel = payMth === m;
          const accent = isOnline ? (h.gn || "#16a34a") : h.ac;
          return (
          <button key={m} onClick={() => { setPayMth(m); setUpiOpened(false); }} style={{
            flex: 1, padding: 10, borderRadius: 10,
            border: `1px solid ${sel ? accent : h.bd}`,
            background: sel ? `${accent}15` : "transparent",
            color: sel ? accent : h.sc, cursor: "pointer",
            fontSize: 12, fontWeight: sel ? 700 : 500
          }}>{isOnline ? (t.payMethodOnline || "Online") : m === "upi" ? (t.upi || "UPI") : m === "cash" ? (t.cash || "Cash") : (t.bank || "Bank")}</button>
        );})}
      </div>

      {payMth === "upi" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, textAlign: "center", marginBottom: 12, border: `1px solid ${h.bd}` }}>
        {isValidUpiId(settings?.upi_id) ? (() => {
          // SECURITY: build via URLSearchParams + strict UPI VPA validation —
          // an attacker-set upi_id like "victim@upi&am=99999&x=" cannot
          // override the amount or sneak in extra params.
          const upiUrl = buildUpiUrl({
            upi_id: settings.upi_id,
            payee_name: settings?.upi_name || "Owner",
            amount,
            note: `Rent - ${me?.name || "Tenant"}`,
          });
          // buildUpiUrl returns null when the amount is 0 / missing /
          // non-finite. Without this guard, <QRCode value={null} /> crashed
          // the whole page with "Cannot read properties of null (reading
          // 'length')". Show a friendly prompt instead.
          if (!upiUrl) {
            return (
              <div style={{ color: h.sc, fontSize: 12, padding: 10, textAlign: "center" }}>
                {t.enterAmountForQr || "Enter a valid amount above to see the UPI QR code."}
              </div>
            );
          }
          return <>
            <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>{!upiOpened ? (t.upiStep1 || "Step 1: Scan QR or tap below to open your UPI app") : (t.upiStep2 || "Step 2: After paying, tap 'I've Paid' to notify landlord")}</div>
            <div style={{ background: "white", padding: 10, borderRadius: 12, margin: "0 auto 10px", width: "fit-content" }}>
              <QRCode value={upiUrl} size={128} />
            </div>
            {!upiOpened ? (
              <a href={upiUrl}
                onClick={(e) => { if (!(Number(amount) > 0)) { e.preventDefault(); flash(t.fillRequired || "Please enter a valid amount", "error"); return; } setUpiOpened(true); }}
                style={{ background: h.ac, color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "block", marginTop: 8, padding: "10px 16px", borderRadius: 10, textAlign: "center" }}>
                {(t.openUpiApp || "Open UPI App")} — ₹{Number(amount).toLocaleString()}
              </a>
            ) : (
              <button onClick={onConfirmUpi} disabled={confirmDisabled}
                style={{ width: "100%", background: confirmDisabled ? "#9ca3af" : (h.gn || "#16a34a"), color: "#fff", fontSize: 13, fontWeight: 600, border: "none", marginTop: 8, padding: "12px 16px", borderRadius: 10, cursor: confirmDisabled ? "not-allowed" : "pointer" }}>
                {"✓ " + (t.iHavePaid || "I've Paid")}
              </button>
            )}
            <div style={{ color: h.sc, fontSize: 11, marginTop: 6 }}>{t.tapUpi || "Scan the QR or tap to open your UPI app"}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6 }}>
              <span style={{ color: h.sc, fontSize: 10 }}>UPI ID:</span>
              <span style={{ color: h.tx, fontSize: 11, fontWeight: 500 }}>{settings?.upi_id}</span>
              <CopyBtn value={settings?.upi_id} label="UPI ID" h={h} />
            </div>
          </>;
        })() : <div style={{ color: h.rd, fontSize: 12, padding: 10 }}>{t.upiNotLoaded || "Landlord hasn't configured a UPI ID yet. Please use Cash or Bank."}</div>}
      </div>}

      {payMth === "bank" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${h.bd}` }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t.bankTransferDetails || "Bank Transfer Details"}</div>
        {(() => {
          // getBankRows() prefers structured fields, but if the landlord has
          // only saved the legacy single-blob `bank_details`, it client-side
          // parses that blob into the same 4 rows so the tenant always gets
          // per-field copy buttons (Account Name / Number / IFSC / Bank).
          const rows = getBankRows(settings);
          if (rows.length === 0) {
            return <div style={{ color: h.sc, fontSize: 12 }}>{t.notConfigured || "Not configured"}</div>;
          }
          return rows.map(r => (
            <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px dashed ${h.bd}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{r.label}</div>
                <div style={{ color: h.tx, fontSize: 13, fontWeight: 500, wordBreak: "break-all" }}>{r.value}</div>
              </div>
              <CopyBtn value={r.value} label={r.label} h={h} />
            </div>
          ));
        })()}
      </div>}

      {payMth === "cash" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${h.bd}` }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>💵 {t.cashPayment || "Cash Payment"}</div>
        <div style={{ color: h.sc, fontSize: 12 }}>{t.cashHint || "Hand over the cash to your landlord and tap Submit to notify them."}</div>
      </div>}

      {payMth === "online" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${h.bd}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 16 }}>⚡</span>
          <div style={{ color: h.tx, fontSize: 13, fontWeight: 700 }}>{t.payOnlineTitle || "Pay instantly online"}</div>
        </div>
        <div style={{ color: h.sc, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          {t.payOnlineHint || "Pay by UPI, card, or net-banking. The money goes straight to your landlord and is marked paid instantly — no approval needed."}
        </div>
        {(rentRows.length > 1 || maintRows.length > 0) && <div style={{ color: h.wn || "#d97706", fontSize: 11, marginBottom: 10, lineHeight: 1.5 }}>
          {(t.payOnlineRowNote || "This pays {mo} in full (₹{amt}). Other dues can still be paid by UPI, Cash, or Bank.").replace("{mo}", dp?.mo || "this month").replace("{amt}", onlineAmt.toLocaleString())}
        </div>}
        <button onClick={onOnline} disabled={cfBusy || !(onlineAmount > 0)}
          style={{ width: "100%", padding: "14px 20px", borderRadius: 12, background: (cfBusy || !(onlineAmount > 0)) ? "#9ca3af" : `linear-gradient(135deg, ${h.gn || "#16a34a"}, #15803d)`, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: (cfBusy || !(onlineAmount > 0)) ? "not-allowed" : "pointer", boxShadow: !(cfBusy || !(onlineAmount > 0)) ? `0 4px 12px ${(h.gn || "#16a34a")}40` : "none", transition: "all 0.2s" }}>
          {cfBusy ? (t.openingCheckout || "Opening secure checkout…") : `${t.payOnlineCta || "Pay Online"} — ₹${Number(onlineAmount).toLocaleString()}`}
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginTop: 8 }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: h.tt }} aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
          <span style={{ color: h.tt, fontSize: 10 }}>{t.poweredByCashfree || "Secured by Cashfree"}</span>
        </div>
      </div>}

      {(payMth === "cash" || payMth === "bank") && <button onClick={onConfirmNonUpi} disabled={confirmDisabled}
        style={{ width: "100%", padding: "14px 20px", borderRadius: 12, background: confirmDisabled ? "#9ca3af" : `linear-gradient(135deg, ${h.ac}, #1d4ed8)`, color: "#fff", fontSize: 15, fontWeight: 700, border: "none", cursor: confirmDisabled ? "not-allowed" : "pointer", boxShadow: !confirmDisabled ? `0 4px 12px ${h.ac}40` : "none", transition: "all 0.2s" }}>
        {submitting ? (t.submitting || "Submitting...") : `${t.submitPay || "Submit Payment"} — ₹${Number(amount).toLocaleString()}`}
      </button>}

      {payMth !== "online" && <div style={{ color: h.sc, fontSize: 10, marginTop: 8, textAlign: "center" }}>💡 {t.payVerifyMsg || "Payment will reflect only after landlord verifies and approves"}</div>}
    </>
  );

  // ── Online pay (Cashfree) handler ─────────────────────────────────
  // Lazily load the Cashfree v3 SDK, create an order on the LANDLORD's
  // own account, then full-page-redirect to Cashfree's hosted checkout.
  // The iframe modal is intentionally avoided (the page's Feature Policy
  // blocks encrypted-media → "No available adapters"). After payment
  // Cashfree returns to /api/payments/cashfree-return, which settles the
  // payment server-side and bounces back with ?rent=success.
  // NB: plain functions (not hooks) — safe to declare before the guards.
  const loadCashfreeSdk = () => new Promise((resolve, reject) => {
    if (window.Cashfree) return resolve();
    const SRC = "https://sdk.cashfree.com/js/v3/cashfree.js";
    const existing = document.querySelector(`script[src="${SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("sdk")));
      if (window.Cashfree) resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = SRC; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("sdk"));
    document.head.appendChild(s);
  });

  const payOnline = async () => {
    const row = rentRows[0] || maintRows[0];
    if (cfBusy || !row) return;
    setCfBusy(true);
    try {
      const d = await api.createCashfreeRentOrder({ paymentId: row.id, tenantName: me?.name });
      // Server reconciled a stranded earlier payment — it's already paid.
      if (d && d.success && d.alreadyPaid) {
        flash(t.rentAlreadyPaid || "This month is already paid ✓");
        if (refreshData) { try { await refreshData(); } catch (_) {} }
        go("dash");
        return;
      }
      if (!d || !d.success || d.provider !== "cashfree" || !d.payment_session_id) {
        flash(d?.detail || d?.error || (t.onlinePayUnavailable || "Couldn't start online payment. Please use UPI, Cash, or Bank."), "error");
        setCfBusy(false);
        return;
      }
      try { await loadCashfreeSdk(); }
      catch (_) { flash(t.billingScriptError || "Payment service unavailable. Check your connection.", "error"); setCfBusy(false); return; }
      if (!window.Cashfree) { flash(t.billingOrderError || "Couldn't load checkout. Please try again.", "error"); setCfBusy(false); return; }
      const cf = window.Cashfree({ mode: d.cashfree_mode === "production" ? "production" : "sandbox" });
      const result = await cf.checkout({ paymentSessionId: d.payment_session_id, redirectTarget: "_self" });
      // With _self the tab normally navigates away before this resolves;
      // if it returns with an error, the redirect failed — surface it.
      if (result && result.error) {
        flash(t.billingOrderError || "Couldn't open Cashfree checkout. Please try again.", "error");
        setCfBusy(false);
      }
    } catch (e) {
      flash("Error: " + (e.message || "Could not start payment"), "error");
      setCfBusy(false);
    }
  };

  // Pay an ADVANCE online (advance-only mode, no due row). Creates a
  // Cashfree order for the entered amount on the LANDLORD's account; it
  // settles as advance credit on return (server settleCashfreeRent's
  // isAdvance path), so no landlord approval is needed.
  const payOnlineAdvance = async () => {
    const amt = Number(payAmt);
    if (cfBusy) return;
    if (!(amt >= 100)) { flash(t.minAdvance || "Minimum advance: ₹100", "error"); return; }
    setCfBusy(true);
    try {
      const d = await api.createCashfreeAdvanceOrder({ amount: amt });
      if (!d || !d.success || d.provider !== "cashfree" || !d.payment_session_id) {
        flash(d?.detail || d?.error || (t.onlinePayUnavailable || "Couldn't start online payment. Please use UPI, Cash, or Bank."), "error");
        setCfBusy(false);
        return;
      }
      try { await loadCashfreeSdk(); }
      catch (_) { flash(t.billingScriptError || "Payment service unavailable. Check your connection.", "error"); setCfBusy(false); return; }
      if (!window.Cashfree) { flash(t.billingOrderError || "Couldn't load checkout. Please try again.", "error"); setCfBusy(false); return; }
      const cf = window.Cashfree({ mode: d.cashfree_mode === "production" ? "production" : "sandbox" });
      const result = await cf.checkout({ paymentSessionId: d.payment_session_id, redirectTarget: "_self" });
      if (result && result.error) {
        flash(t.billingOrderError || "Couldn't open Cashfree checkout. Please try again.", "error");
        setCfBusy(false);
      }
    } catch (e) {
      flash("Error: " + (e.message || "Could not start payment"), "error");
      setCfBusy(false);
    }
  };

  // ── UNLINKED-TENANT GUARD ─────────────────────────────────────────
  // A tenant whose tenants.org_id is NULL (or 'org_default') has no
  // landlord to send a payment to. The server now rejects their POST
  // /payments with 403 (server/routes/payments.js), but if we let
  // them tap Submit anyway they get a confusing error toast. Worse,
  // before the server fix shipped, the local React state showed a
  // pending payment and the addNotif call leaked into org_default's
  // approval queue with a "?" name. Block at render time so the
  // tenant sees a clear "you're not linked yet" page instead of the
  // payment form.
  //
  // NB: this check must come AFTER all hooks have been declared above
  // (React rules-of-hooks). It's safe here because we only render this
  // page conditionally — no hooks declared below this point.
  const isLinked =
    !!me && (
      // Strongest signal: live tenants.org_id is a real org
      (me.org_id && me.org_id !== 'org_default') ||
      // Fallbacks for older sync responses missing org_id
      !!me.pid ||
      pays.some(x => x.tid === ct)
    );
  if (!isLinked) {
    return (
      <div style={{ paddingTop: 16 }}>
        <button onClick={() => go("dash")}
          style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>
          &larr; {t.back || "Back"}
        </button>
        <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{t.payNow || "Pay Now"}</h2>
        <div style={{ ...crd(h), padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔗</div>
          <div style={{ color: h.tx, fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
            {t.notLinkedTitle || "Not linked to a landlord yet"}
          </div>
          <div style={{ color: h.sc, fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
            {t.notLinkedBody ||
              "You're not currently linked to any landlord, so there's nobody to receive your payment. Ask your landlord to send you an invitation — once you accept it, the Pay Now page will show your rent details."}
          </div>
          <button onClick={() => go("dash")}
            style={{ background: h.ac, color: "#fff", border: "none", padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {t.back || "Back to Dashboard"}
          </button>
        </div>
      </div>
    );
  }

  // ── Advance-only mode (no due payment) ────────────────────────────
  if (!dp) {
    const advAmt = Number(payAmt);
    const advValid = advAmt >= 100;
    const lastPaid = pays.filter(x => x.tid === ct && x.st === "paid").sort((a, b) => monthSortValue(b.mo) - monthSortValue(a.mo))[0];

    const submitAdvance = async () => {
      if (submitting || !advValid || payLockRef.current) return;
      if (payMth === "upi" && !settings?.upi_id) { flash(t.upiNotLoaded || "Landlord hasn't configured UPI", "error"); return; }
      payLockRef.current = true;
      setSubmitting(true);
      try {
        const now = new Date();
        const nextMo = `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][now.getMonth()]} ${String(now.getFullYear()).slice(-2)}`;
        const advPayId = `pay_adv_${Date.now()}`;
        const dtStr = now.toISOString().slice(0, 10);
        const newPay = {
          id: advPayId, tid: ct, amt: 0, com: 0, mo: nextMo, mth: payMth,
          st: "due", dt: dtStr,
          pending: { amt: advAmt, mth: payMth, dt: dtStr, status: "waiting" },
          txns: [{ id: `tx${Date.now()}`, tid: ct, amt: advAmt, mth: payMth, dt: dtStr, type: "advance", status: "pending", mo: nextMo, totalWas: 0 }]
        };
        // Server-first, then optimistic UI: if the save fails (e.g. the
        // tenant got unlinked between page load and tap), show a clear
        // error and DON'T paint a fake pending row in their dashboard.
        // Also block the addNotif fan-out so we don't spray a stranger's
        // payment into a default-org landlord's queue.
        const saveRes = await api.savePayment(newPay);
        // Server returns {success: true} on success; {error: '...'} on
        // 403/4xx (no success field); {success: false} on network blip.
        // Treat anything that isn't explicitly success: true as failure.
        if (!saveRes || saveRes.success !== true) {
          throw new Error(saveRes?.error || saveRes?.message || "Server rejected the payment. You may not be linked to a landlord.");
        }
        setPays([...pays, newPay]);
        addNotif("landlord", null, `💰 ${me?.name} submitted advance ₹${advAmt.toLocaleString()} (${payMth.toUpperCase()}) — awaiting approval`, "payment");
        flash(t.paySubmitted || `Advance ₹${advAmt.toLocaleString()} submitted. Awaiting landlord approval.`);
        go("dash");
      } catch (err) {
        flash("Error: " + (err.message || "Could not save payment"));
        setSubmitting(false);
        payLockRef.current = false;
      }
    };

    return <div style={{ paddingTop: 16 }}>
      <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{t.payAdvance || "Pay Advance"}</h2>
      <div style={{ ...crd(h), padding: 20 }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 50, height: 50, borderRadius: 25, background: `${h.ac}15`, marginBottom: 8 }}><span style={{ fontSize: 24 }}>💰</span></div>
          <div style={{ color: "#16a34a", fontWeight: 600, fontSize: 14 }}>✓ {t.allCleared || "All dues cleared!"}</div>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{t.payAdvanceHint || "Pay advance for upcoming months"}</div>
          {lastPaid && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{t.lastPaid || "Last paid"}: {lastPaid.mo} — ₹{(lastPaid.amt + (lastPaid.com || 0)).toLocaleString()}</div>}
        </div>

        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.enterAdvanceAmt || "Enter Advance Amount"}</label>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: h.tx, fontSize: 18, fontWeight: 700 }}>₹</span>
          <input type="number" value={payAmt} onChange={e => setPayAmt(Math.max(0, Number(e.target.value)))} style={{ ...inpS(h), width: "100%", paddingLeft: 36, fontSize: 20, fontWeight: 700, border: `1.5px solid ${h.ac}`, background: `${h.ac}08` }} />
        </div>
        {advAmt >= 100 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, padding: "8px 12px", background: `${h.ac}08`, borderRadius: 10, border: `1px solid ${h.ac}30` }}>
          <span style={{ fontSize: 14 }}>💰</span>
          <span style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.advanceCreditMsg || `Advance ₹${advAmt.toLocaleString()} will be credited to your account`}</span>
        </div>}
        {advAmt > 0 && advAmt < 100 && <div style={{ color: "#dc2626", fontSize: 11, marginBottom: 8 }}>{t.minAdvance || "Minimum advance: ₹100"}</div>}

        {renderMethodPanel({
          amount: advAmt,
          onConfirmUpi: submitAdvance,
          onConfirmNonUpi: submitAdvance,
          confirmDisabled: submitting || !advValid,
          onOnline: payOnlineAdvance,
          onlineAmount: advAmt
        })}
      </div>
    </div>;
  }

  // ── Normal payment mode (due payment exists) ──────────────────────
  const isPartial = payType === "partial";
  const isAdvance = payType === "advance";
  const isValid = payType === "full" ? tot >= 1
    : payType === "partial" ? (payAmt >= 1 && payAmt < tot)
    : payType === "advance" ? (payAmt > tot)
    : payAmt >= 1;

  const handleTypeChange = (type) => {
    setPayType(type);
    setUpiOpened(false);
    if (type === "full") setPayAmt(tot);
    else if (type === "partial") setPayAmt(Math.round(tot / 2));
    else if (type === "advance") setPayAmt(tot + 1000);
  };

  // ── Manual Payment Handler (records pending, awaits landlord approval) ─
  // Allocates the submitted amount across all underlying rows:
  //   • Smallest maintenance first (so a small partial knocks out small bills)
  //   • Rent row last
  //   • For "advance", any leftover after covering everything goes onto the
  //     rent row's pending amount as advance credit.
  const submitPayment = async () => {
    if (submitting || !isValid || payLockRef.current) return;
    if (payMth === "upi" && !settings?.upi_id) { flash(t.upiNotLoaded || "Landlord hasn't configured UPI", "error"); return; }
    payLockRef.current = true;
    setSubmitting(true);
    try {
      const amt = Number(payAmt);
      const now = new Date();
      const dtStr = now.toISOString().slice(0, 10);
      // Allocation order: smallest maint first, then rent months oldest
      // first. Skip rows that already have a pending submission (don't
      // double-submit).
      const orderedRows = [
        ...maintRows.filter(r => !r.pending).sort((a, b) => ((a.amt || 0) + (a.com || 0)) - ((b.amt || 0) + (b.com || 0))),
        ...rentRows.filter(r => !r.pending)
      ];
      let remaining = amt;
      const updates = [];
      const baseTs = Date.now();
      // batchId links every row's pending+txn from this single submission so
      // the landlord PayPage can group them under one "₹{total} batch" banner
      // and offer an "Approve all" affordance.
      const batchId = `bp_${baseTs}`;
      for (let i = 0; i < orderedRows.length; i++) {
        if (remaining <= 0) break;
        const row = orderedRows[i];
        const rowTotal = (row.amt || 0) + (row.com || 0);
        const allocated = Math.min(remaining, rowTotal);
        remaining -= allocated;
        const txn = {
          id: `tx${baseTs}_${i}`, tid: ct, amt: allocated, mth: payMth, dt: dtStr,
          type: payType, status: "pending", mo: row.mo, totalWas: rowTotal,
          batchId, batchTotal: amt
        };
        updates.push({
          ...row,
          pending: { amt: allocated, mth: payMth, dt: dtStr, status: "waiting", batchId, batchTotal: amt },
          txns: [...(row.txns || []), txn]
        });
      }
      // Advance: any leftover after covering all dues becomes extra credit
      // on the newest rent month's pending submission.
      if (payType === "advance" && remaining > 0) {
        const rentIds = new Set(rentRows.map(r => r.id));
        const rentUpdates = updates.filter(u => rentIds.has(u.id));
        const rentUpdate = rentUpdates[rentUpdates.length - 1];
        if (rentUpdate) {
          rentUpdate.pending.amt += remaining;
          rentUpdate.txns[rentUpdate.txns.length - 1].amt += remaining;
        }
      }
      // Nothing allocatable — every row already carries a pending
      // submission (or there's nothing due). Abort loudly instead of
      // showing a success toast for a payment that was never recorded.
      if (updates.length === 0) {
        throw new Error("Your earlier payment is still awaiting landlord approval. Please wait for it to be approved before paying again.");
      }
      // Server-first: don't paint a fake pending row in local state if
      // the server is going to reject the write (e.g. tenant got unlinked
      // mid-flow). Run all saves, surface the first failure, abort.
      const saveResults = await Promise.all(updates.map(u => api.savePayment(u)));
      const failed = saveResults.find(r => !r || r.success !== true);
      if (failed) {
        throw new Error(failed?.error || failed?.message || "Server rejected the payment. You may not be linked to a landlord.");
      }
      const updateById = new Map(updates.map(u => [u.id, u]));
      setPays(pays.map(x => updateById.get(x.id) || x));
      const label = payType === "full" ? `full ₹${amt.toLocaleString()}`
        : payType === "advance" ? `advance ₹${amt.toLocaleString()} (₹${Math.max(0, amt - tot).toLocaleString()} extra)`
        : `partial ₹${amt.toLocaleString()} (₹${Math.max(0, tot - amt).toLocaleString()} remaining)`;
      addNotif("landlord", null, `💳 ${me?.name} submitted ${label} (${payMth.toUpperCase()}) — awaiting approval`, "payment");
      flash(t.paySubmitted || `Payment ₹${amt.toLocaleString()} submitted. Awaiting landlord approval.`);
      go("dash");
    } catch (err) {
      flash("Error: " + (err.message || "Could not save payment"));
      setSubmitting(false);
      payLockRef.current = false;
    }
  };

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{t.payNow}</h2>

    <div style={{ ...crd(h), padding: 20 }}>
      {/* ── Total Due ──────────────────────────────────── */}
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ color: h.sc, fontSize: 12 }}>{alreadyPaid > 0 ? (t.remainingDue || "Remaining Due") : (t.totalDue || "Total Due")}</div>
        <div style={{ color: tot > 0 ? h.rd : "#16a34a", fontWeight: 700, fontSize: 28 }}>₹{tot.toLocaleString()}</div>
        <div style={{ color: h.sc, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 4 }}>
          <span>{t.rentAmt} ₹{rentAmt.toLocaleString()}</span> +
          <span onClick={() => setShowCommonSplit(true)} style={{ color: h.ac, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}>{maintRows.length > 0 ? (t.commonAndMaint || "Common & Maint") : t.commonAmt} ₹{commonAndMaint.toLocaleString()}</span>
        </div>
        {alreadyPaid > 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6, padding: "4px 12px", background: "rgba(22,163,74,0.08)", borderRadius: 8 }}>
          <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 600 }}>✅ {t.alreadyPaid || "Already paid"}: ₹{alreadyPaid.toLocaleString()}</span>
          <span style={{ color: h.sc, fontSize: 11 }}>{t.of || "of"} ₹{grossTotal.toLocaleString()}</span>
        </div>}
        {pendingAmt > 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6, padding: "4px 12px", background: "rgba(217,119,6,0.08)", borderRadius: 8 }}>
          <span style={{ color: "#d97706", fontSize: 12, fontWeight: 600 }}>⏳ {t.awaitApproval || "Awaiting approval"}: ₹{pendingAmt.toLocaleString()}</span>
        </div>}
      </div>

      {/* ── Common Charges Modal ───────────────────────── */}
      {showCommonSplit && (() => {
        const cc = myCC;
        const hasItems = cc && cc.items && cc.items.length > 0;
        const ccTotal = cc ? (hasItems ? cc.items.reduce((s, it) => s + Number(it.amount || 0), 0) : (cc.eb + cc.cl)) : 0;
        return <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: h.bg, borderRadius: 20, width: "100%", maxWidth: 360, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.2)", position: "relative" }}>
          <button onClick={() => setShowCommonSplit(false)} style={{ position: "absolute", top: 16, right: 16, background: `${h.bd}50`, border: "none", width: 30, height: 30, borderRadius: 15, color: h.tx, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ width: 50, height: 50, borderRadius: 25, background: `${h.ac}15`, color: h.ac, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}><Ic n="zap" s={24} c={h.ac} /></div>
            <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{t.common}</h2>
            <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{cc ? (t.breakdownFor + " " + cc.mo) : t.noChargesYet}</div>
          </div>
          {cc ? <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 16, border: `1px solid ${h.bd}` }}>
            {hasItems ? cc.items.map((it, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: idx === cc.items.length - 1 ? 12 : 0, borderBottom: idx === cc.items.length - 1 ? `1px dashed ${h.bd}` : "none" }}><span style={{ color: h.sc, fontSize: 13 }}>{it.name}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{Number(it.amount || 0).toLocaleString()}</span></div>
            )) : <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}><span style={{ color: h.sc, fontSize: 13 }}>{t.electricity}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{cc.eb.toLocaleString()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: 12, borderBottom: `1px dashed ${h.bd}` }}><span style={{ color: h.sc, fontSize: 13 }}>{t.cleaningTotal}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{cc.cl.toLocaleString()}</span></div>
            </>}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span style={{ color: h.sc, fontSize: 12, fontWeight: 500 }}>{t.totalBldgBill}</span><span style={{ color: h.tx, fontWeight: 700, fontSize: 12 }}>₹{ccTotal.toLocaleString()}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.dividedBy + " " + cc.n + " " + t.active}</span><span style={{ color: h.ac, fontWeight: 700, fontSize: 16 }}>₹{myShare.toLocaleString()}</span></div>
            {myMaintCharges.length > 0 && <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${h.bd}` }}>
              <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🛠️ {t.perTenantCharges}</div>
              {myMaintCharges.map((mc, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{mc.desc}</span><span style={{ display: "inline-flex", alignItems: "center" }}><span style={{ color: maintChargeSt(mc) === "paid" ? "#16a34a" : h.wn, fontWeight: 600, fontSize: 12 }}>₹{mc.exp_amt.toLocaleString()}</span>{maintChargePill(mc)}</span></div>
              ))}
            </div>}
          </div> : <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 20, border: `1px solid ${h.bd}`, textAlign: "center" }}>
            <div style={{ color: h.sc, fontSize: 13, marginBottom: 8 }}>{t.noChargesMsg}</div>
            <div style={{ color: h.sc, fontSize: 11 }}>{t.chargesWillUpdate}</div>
          </div>}
          {!cc && myMaintCharges.length > 0 && <div style={{ background: `${h.wn}08`, borderRadius: 12, padding: 16, border: `1px solid ${h.wn}30`, marginTop: 12 }}>
            <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🛠️ {t.perTenantCharges}</div>
            {myMaintCharges.map((mc, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{mc.desc}</span><span style={{ display: "inline-flex", alignItems: "center" }}><span style={{ color: maintChargeSt(mc) === "paid" ? "#16a34a" : h.wn, fontWeight: 600, fontSize: 12 }}>₹{mc.exp_amt.toLocaleString()}</span>{maintChargePill(mc)}</span></div>
            ))}
          </div>}
        </div></div>;
      })()}

      {/* ── Payment Type Selector ────────────────────────── */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 8 }}>{t.paymentType || "Payment Type"}</label>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {[
            { key: "full", icon: "✅", label: t.fullPay || "Full Payment", desc: `₹${tot.toLocaleString()}`, color: "#16a34a" },
            { key: "partial", icon: "📝", label: t.partial || "Partial", desc: t.payLess || "Pay less", color: "#f59e0b" },
            { key: "advance", icon: "💰", label: t.advance || "Advance", desc: t.payExtra || "Pay extra", color: h.ac }
          ].map(opt => (
            <button key={opt.key} onClick={() => handleTypeChange(opt.key)} style={{
              flex: 1, padding: "10px 6px", borderRadius: 12, cursor: "pointer",
              border: `2px solid ${payType === opt.key ? opt.color : h.bd}`,
              background: payType === opt.key ? `${opt.color}10` : "transparent",
              textAlign: "center", transition: "all 0.2s"
            }}>
              <div style={{ fontSize: 18, marginBottom: 2 }}>{opt.icon}</div>
              <div style={{ color: payType === opt.key ? opt.color : h.tx, fontSize: 11, fontWeight: 700 }}>{opt.label}</div>
              <div style={{ color: h.sc, fontSize: 9, marginTop: 1 }}>{opt.desc}</div>
            </button>
          ))}
        </div>

        {/* Amount Input */}
        <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>
          {payType === "full" ? (t.amt || "Amount") : payType === "partial" ? (t.enterPartialAmt || "Enter Partial Amount") : (t.enterAdvanceAmt || "Enter Advance Amount")}
        </label>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: h.tx, fontSize: 18, fontWeight: 700 }}>₹</span>
          <input
            type="number"
            value={payAmt}
            readOnly={payType === "full"}
            onChange={e => {
              const val = Math.max(0, Number(e.target.value));
              setPayAmt(val);
              setUpiOpened(false);
            }}
            style={{
              ...inpS(h), width: "100%", paddingLeft: 36,
              fontSize: 20, fontWeight: 700, textAlign: "left",
              border: `1.5px solid ${isPartial ? "#f59e0b" : isAdvance ? h.ac : "#16a34a"}`,
              background: payType === "full" ? "rgba(22,163,74,0.04)" : isPartial ? "rgba(245,158,11,0.04)" : `${h.ac}08`,
              opacity: payType === "full" ? 0.8 : 1
            }}
          />
        </div>

        {/* Info strip based on type */}
        {isPartial && payAmt > 0 && payAmt < tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: "rgba(245,158,11,0.08)", borderRadius: 10, border: "1px solid rgba(245,158,11,0.2)" }}>
          <span style={{ fontSize: 14 }}>⚠️</span>
          <div><div style={{ color: "#d97706", fontSize: 12, fontWeight: 600 }}>{t.partialPayment || "Partial Payment"}</div>
          <div style={{ color: "#b45309", fontSize: 11 }}>{t.remainingDue || "Remaining due"}: ₹{(tot - payAmt).toLocaleString()}</div></div>
        </div>}
        {isPartial && payAmt >= tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 10, border: "1px solid rgba(239,68,68,0.2)" }}>
          <span style={{ fontSize: 14 }}>❌</span>
          <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 500 }}>{t.partialLessThan || "Partial amount must be less than"} ₹{tot.toLocaleString()}</span>
        </div>}
        {isAdvance && payAmt > tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: `${h.ac}08`, borderRadius: 10, border: `1px solid ${h.ac}30` }}>
          <span style={{ fontSize: 14 }}>💰</span>
          <div><div style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.advancePayment || "Advance Payment"}</div>
          <div style={{ color: h.sc, fontSize: 11 }}>₹{tot.toLocaleString()} {t.duePlus || "due +"} ₹{(payAmt - tot).toLocaleString()} {t.advance || "advance"}</div></div>
        </div>}
        {isAdvance && payAmt <= tot && <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 10, border: "1px solid rgba(239,68,68,0.2)" }}>
          <span style={{ fontSize: 14 }}>❌</span>
          <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 500 }}>{t.advanceMoreThan || "Advance must be more than"} ₹{tot.toLocaleString()}</span>
        </div>}
      </div>

      {/* ── Method Picker (UPI QR / Cash / Bank) ────────── */}
      {renderMethodPanel({
        amount: Number(payAmt) || 0,
        onConfirmUpi: submitPayment,
        onConfirmNonUpi: submitPayment,
        confirmDisabled: submitting || !isValid,
        onOnline: payOnline,
        onlineAmount: onlineAmt
      })}
    </div>
  </div>;
}

export default TenPayNow;
